import React, { useEffect, useMemo, useState } from "react";
import { DataRefreshAssistantCluster, Progress, RmBodyZone, Skeleton, useToast } from "randmarcomps";
import { useGetV4PartnerByRouteApplicationIdWarehousesQuery, Warehouse as ApiWarehouse } from "randmar-api-client";
import RmPageHeader from "../components/layout/RmPageHeader";
import WarehouseInfoCard from "../componentsCore/cards/WarehouseInfoCard";
import { useManufacturer } from "../contexts/WithManufacturer";
import withRoot from "../withRoot";

const WarehouseCardSkeleton = () => (
  <div className="p-4 border rounded-lg space-y-3">
    <Skeleton className="h-6 w-1/2" />
    <Skeleton className="h-4 w-3/4" />
    <Skeleton className="h-4 w-full" />
  </div>
);

const WarehouseInformation: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { data: apiWarehouses, isLoading, isFetching, isError, refetch } = useGetV4PartnerByRouteApplicationIdWarehousesQuery(
    { routeApplicationId: applicationId },
    { skip: !applicationId }
  );
  const { toast } = useToast();
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (isError) {
      toast({
        variant: "destructive",
        title: "Error fetching warehouses",
        description: "There was an issue retrieving warehouse information. Please try again later.",
      });
    }
  }, [isError, toast]);

  const warehouses = useMemo(() => apiWarehouses ?? [], [apiWarehouses]);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    if (apiWarehouses !== undefined) {
      setLastRefreshedAt(new Date());
    }
  }, [apiWarehouses, isFetching]);

  const assistantPrompt = useMemo(() => {
    const warehouseCount = warehouses.length;
    const sampleWarehouses = warehouses
      .slice(0, 5)
      .map((warehouse) => warehouse.Name || warehouse.WarehouseId || "Unknown")
      .join(", ");

    return [
      "You are assisting with the Randmar Manufacturer Portal Warehouse Information page.",
      `Warehouses loaded: ${warehouseCount}`,
      sampleWarehouses ? `Sample warehouses: ${sampleWarehouses}` : "No warehouse records loaded.",
      "",
      "Help me interpret warehouse coverage and identify next operational actions.",
    ].join("\n");
  }, [warehouses]);

  const pageHeader = (
    <RmPageHeader
      title="Warehouse Information"
      subtitle="Review warehouse details and contact points used for manufacturer logistics."
      rightTopActions={(
        <DataRefreshAssistantCluster
          lastRefreshAt={lastRefreshedAt}
          isRefreshing={isFetching}
          onRefresh={async () => {
            await refetch();
          }}
          assistantPromptOptions={{ prompt: assistantPrompt }}
        />
      )}
    />
  );

  if (isLoading) {
    return (
      <>
        {pageHeader}
        <RmBodyZone className="space-y-6 pb-10">
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <WarehouseCardSkeleton />
          <WarehouseCardSkeleton />
        </div>
        </RmBodyZone>
      </>
    );
  }

  if (warehouses.length === 0) {
    return (
      <>
        {pageHeader}
        <RmBodyZone className="space-y-6 pb-10">
        <div className="w-full">
          <div className="rounded-lg border-2 border-dashed bg-white p-12 text-center">
            <p className="text-gray-500">No warehouse information available.</p>
          </div>
        </div>
        </RmBodyZone>
      </>
    );
  }

  return (
    <>
      {pageHeader}
      <RmBodyZone className="space-y-6 pb-10">
      <div className="w-full">
        <Progress value={100} className="mb-5" />
      </div>
      <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
        {warehouses.map((warehouse: ApiWarehouse, index) => (
          <WarehouseInfoCard key={warehouse.IntegrationKeyName ?? warehouse.WarehouseId ?? String(index)} warehouse={warehouse} />
        ))}
      </div>
      </RmBodyZone>
    </>
  );
};

export default withRoot(WarehouseInformation);
